import type {
  Agenda,
  AgendaListResponse,
  Attachment,
  Item,
  PublishResult,
  PublishTarget,
  SpacingProfile,
  Tenant,
  TimelineResult,
} from '../types/agenda';

// In dev, base is '' so requests hit the Vite proxy (/api -> :8765).
// In prod, set VITE_API_BASE_URL if the API lives on a different origin.
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// ─── Auth token injection ────────────────────────────────────────────────────
// The Auth0 access-token getter is async and lives in a React hook. We register
// it here so the framework-agnostic client can attach a bearer token without
// importing React. When unset (auth disabled), requests go out unauthenticated.

type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider | null = null;

export function setTokenProvider(fn: TokenProvider | null): void {
  tokenProvider = fn;
}

// ─── Tenant ID injection ──────────────────────────────────────────────────────
// Similar to token provider, we register a function to get the active tenant ID
// so the client can inject X-Tenant-ID header without importing React context.

type TenantIdProvider = () => string | null;
let tenantIdProvider: TenantIdProvider | null = null;

export function setActiveTenantProvider(fn: TenantIdProvider | null): void {
  tenantIdProvider = fn;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Classifies a thrown save/mutation error as a persistence failure that should
 * hard-block editing — i.e. the change genuinely did not persist and the user
 * must not keep editing against stale state.
 *
 * Blocking: a network error thrown while the browser is online, or an ApiError
 * with status >= 500 (incl. the backend "transaction log write failed"), 401,
 * 403, or any other unexpected 4xx.
 *
 * NOT blocking (handled by the existing recoverable flows): 409 stale /
 * concurrent edit and 404 already-deleted. Offline is handled by the autosave
 * queue, not treated as a failure.
 */
export function isBlockingError(e: unknown): boolean {
  if (e instanceof ApiError) {
    if (e.status === 409 || e.status === 404) return false;
    return e.status >= 500 || e.status === 401 || e.status === 403 || e.status >= 400;
  }
  // Non-ApiError (e.g. fetch network failure): block only if we're online —
  // an offline error is a known transient handled by the offline queue.
  return typeof navigator === 'undefined' || navigator.onLine;
}

/** A user-facing message for a blocking save failure. */
export function blockingMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return 'Your session has expired. Please sign in again.';
    if (e.status === 403) return 'You no longer have permission to edit this agenda.';
    const raw = typeof e.message === 'string' ? e.message : '';
    if (raw.toLowerCase().includes('transaction log')) {
      return 'The server could not record this change to the transaction log, so it was not saved.';
    }
    if (e.status >= 500) return `The server failed to save your change (${e.status}).`;
    return raw || `Your change could not be saved (${e.status}).`;
  }
  return 'Could not reach the server to save your change.';
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  if (tokenProvider) {
    const token = await tokenProvider();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  if (tenantIdProvider) {
    const tenantId = tenantIdProvider();
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
  }

  return headers;
}

async function parseError(res: Response): Promise<never> {
  let msg = `${res.status} ${res.statusText}`;
  let data: unknown;
  try {
    data = await res.json();
    if (data && typeof (data as Record<string, unknown>).error === 'string') {
      msg = (data as Record<string, unknown>).error as string;
    }
  } catch {
    // non-JSON body; keep status text
  }
  if (res.status === 401) msg = 'Your session has expired. Please sign in again.';
  if (res.status === 403) msg = `Permission denied. ${msg}`;
  throw new ApiError(res.status, msg, data);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(await authHeaders()),
    ...((init.headers as Record<string, string>) ?? {}),
  };
  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) await parseError(res);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Fetch a text endpoint with auth headers. */
async function requestText(path: string, init: RequestInit = {}): Promise<string> {
  const headers = { ...(await authHeaders()), ...((init.headers as Record<string, string>) ?? {}) };
  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) await parseError(res);
  return res.text();
}

/** Fetch a binary endpoint as an object URL (caller must revoke it). */
async function requestObjectUrl(path: string, init: RequestInit = {}): Promise<string> {
  const headers = { ...(await authHeaders()), ...((init.headers as Record<string, string>) ?? {}) };
  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) await parseError(res);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export interface ItemPatchResponse {
  agenda: Agenda;
  concurrentEditDetected: boolean;
  /** OAuth subject ID of the editor whose unsaved change was overwritten. */
  overwrotePrincipal?: string;
}

export interface MoveItemResponse {
  source: Agenda;
  destination: Agenda;
}

export interface UserProfile {
  id: string;
  firstName: string;
  updatedAt?: string;
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const api = {
  listAgendas: () => request<AgendaListResponse>('/api/agendas'),

  getAgenda: (id: string) => request<Agenda>(`/api/agendas/${encodeURIComponent(id)}`),

  createAgenda: (agenda: Agenda) =>
    request<Agenda>('/api/agendas', { method: 'POST', body: JSON.stringify(agenda) }),

  updateAgenda: (agenda: Agenda) =>
    request<Agenda>(`/api/agendas/${encodeURIComponent(agenda.id)}`, {
      method: 'PUT',
      body: JSON.stringify(agenda),
    }),

  deleteAgenda: (id: string) =>
    request<void>(`/api/agendas/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ── Fine-grained item operations ─────────────────────────────────────────

  patchAgendaMeta: (agendaId: string, patch: Partial<Agenda>) =>
    request<Agenda>(`/api/agendas/${encodeURIComponent(agendaId)}/meta`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  patchItem: (
    agendaId: string,
    itemId: string,
    item: Item,
    originalUpdatedAt: string | undefined,
  ) =>
    request<ItemPatchResponse>(
      `/api/agendas/${encodeURIComponent(agendaId)}/items/${encodeURIComponent(itemId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ item, originalUpdatedAt }),
      },
    ),

  addItem: (
    agendaId: string,
    item: Item,
    afterID: string | null,
    agendaVersion: number,
    beforeID?: string | null,
  ) =>
    request<Agenda>(`/api/agendas/${encodeURIComponent(agendaId)}/items`, {
      method: 'POST',
      body: JSON.stringify({ item, afterID, beforeID, agendaVersion }),
    }),

  deleteItem: (agendaId: string, itemId: string, agendaVersion: number) =>
    request<Agenda>(
      `/api/agendas/${encodeURIComponent(agendaId)}/items/${encodeURIComponent(itemId)}?v=${agendaVersion}`,
      { method: 'DELETE' },
    ),

  reorderItems: (agendaId: string, itemIDs: string[], agendaVersion: number) =>
    request<Agenda>(`/api/agendas/${encodeURIComponent(agendaId)}/items/order`, {
      method: 'PUT',
      body: JSON.stringify({ itemIDs, agendaVersion }),
    }),

  /**
   * Move a single item from one agenda to another of the same meeting type.
   * The server removes it from the source then appends it to the destination
   * (before the destination's insertion-point sentinel), returning both
   * updated agendas.
   */
  moveItem: (
    srcAgendaId: string,
    itemId: string,
    destAgendaID: string,
    srcAgendaVersion: number,
  ) =>
    request<MoveItemResponse>(
      `/api/agendas/${encodeURIComponent(srcAgendaId)}/items/${encodeURIComponent(itemId)}/move`,
      {
        method: 'POST',
        body: JSON.stringify({ destAgendaID, srcAgendaVersion }),
      },
    ),

  /**
   * Upload a file (or inline-authored Blob) as an attachment for an agenda.
   * Returns the attachment descriptor to store on the item. The public `url`
   * is informational; rendering recomputes links from hash + filename.
   */
  uploadAttachment: async (
    agendaId: string,
    file: File | Blob,
    filename?: string,
  ): Promise<Attachment & { url: string }> => {
    const form = new FormData();
    const name = filename ?? (file instanceof File ? file.name : 'attachment');
    form.append('file', file, name);
    const res = await fetch(
      `${BASE_URL}/api/agendas/${encodeURIComponent(agendaId)}/attachments`,
      { method: 'POST', headers: await authHeaders(), body: form },
    );
    if (!res.ok) await parseError(res);
    return res.json() as Promise<Attachment & { url: string }>;
  },

  // ── Read-only / compute ───────────────────────────────────────────────────

  computeTimeline: (agenda: Agenda) =>
    request<TimelineResult>('/api/agendas/timeline/compute', {
      method: 'POST',
      body: JSON.stringify(agenda),
    }),

  publish: (id: string, targets: PublishTarget[], profile: SpacingProfile = 'medium') =>
    request<PublishResult>(`/api/agendas/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify({ targets, profile }),
    }),

  listOutputs: (id: string) =>
    request<Record<string, string>>(`/api/agendas/${encodeURIComponent(id)}/outputs`),

  /** Generate a PDF preview rendered to PNG; returns an object URL. */
  previewPng: (
    id: string,
    opts: { profile?: SpacingProfile; size?: 'letter' | '13x19'; page?: number } = {},
  ) => {
    const q = new URLSearchParams({
      profile: opts.profile ?? 'medium',
      size: opts.size ?? 'letter',
      page: String(opts.page ?? 1),
    });
    return requestObjectUrl(`/api/agendas/${encodeURIComponent(id)}/preview?${q}`, {
      method: 'POST',
    });
  },

  /** Generate a PDF preview with all pages as base64-encoded PNGs. */
  previewAllPngs: (
    id: string,
    opts: { profile?: SpacingProfile; size?: 'letter' | '13x19' } = {},
  ) => {
    const q = new URLSearchParams({
      profile: opts.profile ?? 'medium',
      size: opts.size ?? 'letter',
    });
    return request<{ pages: string[] }>(
      `/api/agendas/${encodeURIComponent(id)}/preview/all?${q}`,
      { method: 'POST' },
    );
  },

  /** Download a published output as an object URL (sends auth header). */
  downloadOutput: (path: string) => requestObjectUrl(path),

  /** Fetch a published output as plain text (sends auth header). */
  fetchOutputText: (path: string) => requestText(path),

  ghostStatus: () =>
    request<{ available: boolean }>('/api/ghost/status').then((r) => r.available),

  listEmoji: () => request<string[]>('/api/emoji'),

  // ── User directory ────────────────────────────────────────────────────────

  getMe: () => request<UserProfile>('/api/me'),

  updateMe: (firstName: string) =>
    request<UserProfile>('/api/me', {
      method: 'PUT',
      body: JSON.stringify({ firstName }),
    }),

  listUsers: () => request<UserProfile[]>('/api/users'),

  // ── Multi-tenant ──────────────────────────────────────────────────────────

  listTenants: () => request<Tenant[]>('/api/tenants'),
};
